import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Plus, Download } from 'lucide-react';
import FileSaver from 'file-saver';
import Papa from 'papaparse';

const LedgerTable = ({ ledgers }) => {
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const userRole = localStorage.getItem("role");

  const filteredLedgers = Array.isArray(ledgers)
    ? ledgers.filter((ledger) => {
        const matchesSearch =
          ledger.partyId.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          ledger.partyId.mobile.includes(searchQuery);
        const matchesType =
          transactionTypeFilter === 'all' || ledger.transactionType === transactionTypeFilter;
        return matchesSearch && matchesType;
      })
    : [];

  const sortedLedgers = [...filteredLedgers].sort(
    (a, b) => new Date(b.transactionDate) - new Date(a.transactionDate)
  );

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedItems(sortedLedgers.map((ledger) => ledger._id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleRowSelect = (ledgerId) => {
    setSelectedItems((prevSelected) =>
      prevSelected.includes(ledgerId)
        ? prevSelected.filter((id) => id !== ledgerId)
        : [...prevSelected, ledgerId]
    );
  };

  const handleDownload = () => {
    const csvData = Papa.unparse({
      fields: [
        "Party Name",
        "Mobile",
        "Transaction Date",
 "Credit",
        "Debit",
        "Amount",
        "Payment Mode",
        "Description",
      ],
      data: ledgers.map((ledger) => ({
        "Party Name": ledger.partyId.name,
        "Mobile": ledger.partyId.mobile,
        "Transaction Date": new Date(ledger.transactionDate).toLocaleDateString(),
        "Credit": ledger.transactionType === 'credit' ? ledger.amount.toFixed(2) : '-',
        "Debit": ledger.transactionType === 'debit' ? ledger.amount.toFixed(2) : '-',
        // "Amount": ledger.amount.toFixed(2),
        "Payment Mode": ledger.paymentMode,
        "Description": ledger.description || "-",
      })),
    });
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    FileSaver.saveAs(blob, 'ledger.csv');
  };

  return (
    <div className="max-w-8xl mx-auto px-0 lg:px-8 py-8">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ledger</h1>
            <p className="mt-1 text-xs text-gray-500">
              Total transactions ({ledgers.length})
            </p>
          </div>
          <div className="flex gap-3">
            {userRole === 'admin' ? (
              <button
                onClick={handleDownload}
                className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-md transition-colors duration-150"
              >
                <Download size={18} className="mr-2" />
                Download
              </button>
            ) : (
              <></>
              
              // <Link
              //   to="/ledger/add"
              //   className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors duration-150"
              // >
              //   <Plus size={18} className="mr-2" />
              //   Add Transaction
              // </Link>
            )}
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
          <div className="relative w-full sm:w-96">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Search by party name or mobile..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150"
            />
          </div>
          <div className="flex items-center gap-3 bg-white p-2 rounded-lg border border-gray-200 shadow-sm">
            {['all', 'credit', 'debit'].map((type) => (
              <button
                key={type}
                onClick={() => setTransactionTypeFilter(type)}
                className={`
                  px-4 py-2 rounded-md text-xs font-medium transition-all duration-200
                  ${
                    transactionTypeFilter === type
                      ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-200'
                      : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50 hover:shadow-sm'
                  }
                  focus:outline-none focus:ring-2 focus:ring-blue-300
                `}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
        <div className="min-w-full overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={
                      sortedLedgers.length > 0 &&
                      sortedLedgers.every((ledger) => selectedItems.includes(ledger._id))
                    }
                    onChange={handleSelectAll}
                    className="form-checkbox h-4 w-4 text-blue-600 cursor-pointer"
                  />
                </th> */}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Party Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mobile
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transaction Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Credit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Debit
                </th>
                {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th> */}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Mode
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedLedgers.map((ledger) => (
                <tr
                  key={ledger._id}
                  className="hover:bg-gray-50 transition-colors duration-150"
                >
                  {/* <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(ledger._id)}
                      onChange={() => handleRowSelect(ledger._id)}
                      className="form-checkbox h-4 w-4 text-blue-600 cursor-pointer"
                    />
                  </td> */}
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                    {ledger.partyId.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                    {ledger.partyId.mobile}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                    {new Date(ledger.transactionDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                    {ledger.transactionType === 'credit' ? ledger.amount.toFixed(2) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                    {ledger.transactionType === 'debit' ? ledger.amount.toFixed(2) : '-'}
                  </td>
                  {/* <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                    {ledger.amount.toFixed(2)}
                  </td> */}
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                    {ledger.paymentMode}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                    {ledger.description || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LedgerTable;